/**
 * Sidebar navigation configuration.
 *
 * One source of truth for the workspace sidebar — order, grouping, icons,
 * routes, and badge state. The sidebar component just renders this.
 *
 * Spec: prototype/home/dashboard.html `.nav` block (§11.6 — workspace primary
 * nav max 2 items, then labelled groups). Order matches the prototype.
 *
 * Adding a route to the sidebar: append an entry to the right group.
 * Removing one: take it out and the badge UI for it disappears too.
 *
 * Permission gating (ADR-0030): each item carries an optional `permission`
 * string. `visibleNavGroups()` hides items the user's granted set doesn't
 * cover and drops groups that become empty as a result. Groups with no
 * `permission` on any item are always shown. The `*` wildcard (owner) sees
 * everything. Server gates (`permissionGate`) are always the floor — this
 * is display-only.
 */
import type { LucideIcon } from "lucide-react";
import {
  Sparkles,
  LayoutDashboard,
  Receipt,
  HandCoins,
  IndianRupee,
  Users,
  Package,
  ClipboardList,
  Inbox,
  ShoppingCart,
  Truck,
  Landmark,
  GitCompareArrows,
  BookOpenCheck,
  FileSpreadsheet,
  Percent,
  Settings,
  Wallet,
  ArrowLeftRight,
  Shirt,
  Factory,
  Tablet,
  UsersRound,
  CalendarClock,
  HelpCircle,
  LifeBuoy,
  Boxes,
  Building2,
  SlidersHorizontal,
  FolderOpen,
  Store,
  ScanLine,
  FileBarChart,
  NotebookText,
  Layers,
  FlaskConical,
  Undo2,
  FileText,
} from "lucide-react";

/**
 * External destinations for the footer Help / Support links. Placeholders for
 * now — swap for the real help-center URL and support channel when they land
 * (support is premium per system-decisions §1 / product principle 1).
 */
export const HELP_URL = "https://help.flowtrail.ai";
export const SUPPORT_URL = "mailto:support@flowtrail.ai";

/**
 * Wire-routes (the actual ones we've built). Used to disable nav items
 * for surfaces that haven't landed yet — keeps the prototype's information
 * architecture visible while being honest about what's clickable.
 *
 * V1 design: keep the full nav visible but mark missing routes
 * `disabled: true` so the user sees the IA. When a vertical lands, flip
 * the flag and remove the cosmetic suffix.
 */
const LIVE_ROUTES = new Set<string>([
  "/",
  "/ask",
  "/sales/invoices",
  "/sales/proformas",
  "/procurement/purchase-orders",
  "/procurement/goods-receipts",
  "/procurement/bills",
  "/procurement/payments",
  "/sales/customers",
  "/sales/collections",
  "/sales/receipts",
  "/compliance/gst-returns",
  "/compliance/tds",
  "/cash/bank-accounts",
  "/cash/petty-cash",
  "/cash/transfers",
  "/cash/reconcile",
  "/cash/movements",
  // "/records/audit-log" removed with its nav item (#249) — this set only ever
  // gates nav rendering, so an entry no item points at is dead weight.
  "/sales/items",
  "/pos",
  "/pos/shifts",
  "/approvals",
  "/procurement/vendors",
  "/payroll/employees",
  "/payroll",
  "/settings",
  "/station",
  "/merchandising/orders",
  "/merchandising/styles",
  "/merchandising/risks",
  "/production/floor",
  "/compliance/itc04",
  "/compliance/books",
  "/inventory/stock",
  "/inventory/warehouses",
  "/inventory/stock-adjustments",
  "/inventory/stock-transfers",
  "/documents",
  "/reports",
  "/store/admin",
  "/store/admin/products",
  "/store/admin/collections",
  "/store/admin/buyers",
  "/store/admin/orders",
  "/store/admin/quotes",
  "/store/admin/sampling",
  "/store/admin/returns",
  "/store/admin/reports",
]);

export interface NavItem {
  /** Path the link points to. Disabled items use `'#'` to keep the link semantic. */
  to: string;
  label: string;
  icon: LucideIcon;
  /** Optional badge — count + tone. Tone matches prototype `.badge` variants (§2.3.1). */
  badge?: {
    count: number;
    tone: "default" | "info" | "warn" | "bad";
  };
  /** AI-tinted item (Ask Flint). Spec §10.2.1: anything sky-coloured in chrome means AI. */
  ai?: boolean;
  /** Surface not yet implemented — render as muted, no link. */
  disabled?: boolean;
  /**
   * External URL (help center, mailto, etc.). When set, the item renders as a
   * new-tab anchor instead of a router link and skips the `LIVE_ROUTES` gate
   * (it leaves the SPA, so `to` is just the placeholder `'#'`).
   */
  external?: string;
  /**
   * Minimum permission required to see this item (ADR-0030). If absent, the
   * item is always shown (unprotected surface). The `*` wildcard (owner) passes
   * every check. `visibleNavGroups()` filters items and drops empty groups.
   */
  permission?: string;
  /**
   * Module entitlement (`company_entitlements.modules`) this single item is
   * gated by — for items that live in an ungated group (e.g. Ask Flint in the
   * top "Workspace" group, gated by the AI add-on). Hidden when the company
   * lacks the module.
   */
  module?: string;
}

export interface NavGroup {
  /** Human label printed above the group; `null` = unlabelled (the workspace primary group). */
  label: string | null;
  /** ARIA label for the inner `<nav>` — required when `label` is null. */
  ariaLabel: string;
  items: NavItem[];
  /**
   * Module entitlement (`company_entitlements.modules`) this group is gated by
   * — the group is hidden unless the active company has it (ADR-0023 §4).
   * Generic groups omit it (always visible). Garment groups set a `garment.*`
   * string so a non-factory workspace never sees them.
   */
  module?: string;
  /**
   * Permission string (ADR-0030) this group is gated by — the group is hidden
   * unless the signed-in member holds it (or holds `*`). Generic groups omit it
   * (always visible). Used for Payroll → `payroll.view`.
   */
  permission?: string;
}

/** Decorate items with `disabled: true` when their route isn't live yet. */
function withLiveCheck(items: NavItem[]): NavItem[] {
  return items.map((item) => {
    if (item.to === "#" || item.disabled) return item;
    if (LIVE_ROUTES.has(item.to)) return item;
    return { ...item, disabled: true };
  });
}

export const NAV_GROUPS: NavGroup[] = [
  {
    label: null,
    ariaLabel: "Workspace",
    items: withLiveCheck([
      { to: "/", label: "Home", icon: LayoutDashboard },
      { to: "/ask", label: "Ask Flint", icon: Sparkles, ai: true, module: "ai.assistant" },
      // Inbox is the founder's approval surface; non-founders see it but it's
      // empty for them — no permission gate so operators can still track status.
      { to: "/approvals", label: "Inbox", icon: Inbox },
    ]),
  },
  {
    label: "Sales",
    ariaLabel: "Sales",
    items: withLiveCheck([
      { to: "/sales/invoices", label: "Invoices", icon: Receipt, permission: "invoices.view" },
      { to: "/sales/proformas", label: "Proformas", icon: FileSpreadsheet, permission: "invoices.view" },
      { to: "/sales/collections", label: "Collections", icon: HandCoins, permission: "receipts.view" },
      { to: "/sales/customers", label: "Customers", icon: Users, permission: "customers.view" },
      { to: "/sales/items", label: "Items", icon: Package, permission: "items.view" },
    ]),
  },
  {
    // Commerce Store — the B2B wholesale storefront admin (V1.0.1). Optional
    // module (`commerce.admin`): the group shows only when the Commerce Store
    // add-on is enabled. @see docs/ecom/prd.md
    label: "Store",
    ariaLabel: "Store",
    module: "commerce.admin",
    // Drives entitlement gating, the launcher tile and the module switcher.
    // The Store module renders its own grouped left sidebar inside the page
    // (`StoreAdminLayout`, mirroring prototype/store/admin/all.html), so its
    // pages pass `hideSectionTabs` and these items don't also appear as
    // topbar tabs. Products is the only module built end-to-end; every other
    // link routes to a "coming soon" page rather than a dead one.
    items: withLiveCheck([
      { to: "/store/admin", label: "Dashboard", icon: Store },
      { to: "/store/admin/products", label: "Products", icon: Package },
      { to: "/store/admin/collections", label: "Collections", icon: Layers },
      { to: "/store/admin/orders", label: "Orders", icon: ClipboardList },
      { to: "/store/admin/quotes", label: "Quotes", icon: FileText },
      { to: "/store/admin/sampling", label: "Sampling", icon: FlaskConical },
      { to: "/store/admin/buyers", label: "Buyers", icon: Users },
      { to: "/store/admin/returns", label: "Returns", icon: Undo2 },
      { to: "/store/admin/reports", label: "Reports", icon: FileBarChart },
    ]),
  },
  {
    label: "POS",
    ariaLabel: "Point of Sale",
    // POS is an optional add-on (migration 0106): the group shows only when the
    // workspace has the `pos.register` module (POS add-on enabled). Per-item
    // access stays on the `pos.*` permissions.
    module: "pos.register",
    permission: "pos.ring",
    // Shift history is FIRST so the rail icon lands on a workspace page (with
    // the section tab bar) — the chrome-less /pos kiosk has no tabs, so making
    // it the default route hid Shift history entirely. Register is launched
    // from the tab / the "Open register" button.
    items: withLiveCheck([
      { to: "/pos/shifts", label: "Shift history", icon: Store, permission: "pos.shift.manage" },
      { to: "/pos", label: "Register", icon: ScanLine, permission: "pos.ring" },
    ]),
  },
  {
    label: "Procurement",
    ariaLabel: "Procurement",
    items: withLiveCheck([
      { to: "/procurement/purchase-orders", label: "Purchase orders", icon: ClipboardList, permission: "purchaseOrders.view" },
      { to: "/procurement/goods-receipts", label: "Goods receipts", icon: Inbox, permission: "goodsReceipts.view" },
      { to: "/procurement/bills", label: "Bills", icon: ShoppingCart, permission: "bills.view" },
      { to: "/procurement/payments", label: "Payments", icon: IndianRupee, permission: "vendorPayments.view" },
      { to: "/procurement/vendors", label: "Vendors", icon: Truck, permission: "vendors.view" },
    ]),
  },
  {
    label: "Inventory",
    ariaLabel: "Inventory",
    items: withLiveCheck([
      { to: "/inventory/stock", label: "Stock", icon: Boxes, permission: "inventory.view" },
      { to: "/inventory/warehouses", label: "Warehouses", icon: Building2, permission: "inventory.view" },
      { to: "/inventory/stock-adjustments", label: "Adjustments", icon: SlidersHorizontal, permission: "inventory.adjust" },
      // Stock transfers are the 1.0-withheld `stock-ops` module (a single factory
      // runs one store in month one; returns in 1.2). Stock / warehouses /
      // adjustments stay in. @see docs/adr/0046-withheld-1.0-modules.md
      { to: "/inventory/stock-transfers", label: "Transfers", icon: ArrowLeftRight, permission: "inventory.transfer", module: "stock-ops" },
    ]),
  },
  {
    // Payroll — 1.0-withheld module (returns in 1.2). Group hidden unless the
    // workspace has the `payroll` entitlement, which no 1.0 plan grants.
    // @see docs/adr/0046-withheld-1.0-modules.md
    label: "Payroll",
    ariaLabel: "Payroll",
    module: "payroll",
    permission: "payroll.view",
    items: withLiveCheck([
      { to: "/payroll/employees", label: "Employees", icon: UsersRound, permission: "employees.view" },
      { to: "/payroll", label: "Payroll runs", icon: CalendarClock, permission: "payroll.view" },
    ]),
  },
  {
    // Cash — bank accounts stay in the 1.0 surface (vendor payments need them),
    // but petty cash / transfers / reconcile are the 1.0-withheld `cash` module
    // (returns in 1.2), so those three items carry the `cash` gate while the
    // group itself stays visible. @see docs/adr/0046-withheld-1.0-modules.md
    label: "Cash",
    ariaLabel: "Cash",
    items: withLiveCheck([
      { to: "/cash/bank-accounts", label: "Bank accounts", icon: Landmark, permission: "bankAccounts.view" },
      { to: "/cash/petty-cash", label: "Petty cash", icon: Wallet, permission: "pettyCash.view", module: "cash" },
      { to: "/cash/transfers", label: "Transfers", icon: ArrowLeftRight, permission: "transfers.record", module: "cash" },
      { to: "/cash/reconcile", label: "Match transactions", icon: GitCompareArrows, permission: "reconcile.view", module: "cash" },
    ]),
  },
  {
    // Garment OS · desk side (ADR-0040) — the merchandiser's surfaces: buyer
    // orders, style files (sampling/costing/PP live inside), critical path.
    // Visible only when the workspace is entitled to `garment.styles`.
    label: "Merchandising",
    ariaLabel: "Merchandising",
    module: "garment.styles",
    items: withLiveCheck([
      // Orders FIRST (ADR-0039): buyers commit order-first; the Style File is
      // the engineering artifact linked afterwards.
      { to: "/merchandising/orders", label: "Orders", icon: NotebookText, permission: "garment.styles.view" },
      { to: "/merchandising/styles", label: "Style files", icon: Shirt, permission: "garment.styles.view" },
    ]),
  },
  {
    // Garment OS · floor side (ADR-0040) — execution surfaces for supervisors
    // and line operators. Same entitlement as Merchandising (one Garment OS
    // add-on); the group-level permission keeps the floor off a pure desk
    // role's screen — the split IS the permission boundary.
    label: "Production",
    ariaLabel: "Production",
    module: "garment.styles",
    permission: "garment.production.view",
    items: withLiveCheck([
      { to: "/production/floor", label: "Floor", icon: Factory, permission: "garment.production.view" },
      { to: "/station", label: "Station Mode", icon: Tablet, permission: "garment.production.record" },
    ]),
  },
  {
    // Documents (DMS) — 1.0-withheld module (returns in 1.2). Group hidden
    // unless the workspace has the `documents` entitlement, which no 1.0 plan
    // grants. @see docs/adr/0046-withheld-1.0-modules.md
    label: "Documents",
    ariaLabel: "Documents",
    module: "documents",
    items: withLiveCheck([{ to: "/documents", label: "Documents", icon: FolderOpen }]),
  },
  {
    label: "Compliance",
    ariaLabel: "Compliance",
    items: withLiveCheck([
      // Books/accounting statements are the 1.0-withheld `books` module (Tally
      // XML export is the 1.0 answer; Books returns in 1.2). GST / TDS / ITC-04
      // stay in. @see docs/adr/0046-withheld-1.0-modules.md
      { to: "/compliance/books", label: "Books", icon: BookOpenCheck, permission: "books.view", module: "books" },
      { to: "/compliance/gst-returns", label: "GST returns", icon: FileSpreadsheet, permission: "gstReturns.view" },
      { to: "/compliance/tds", label: "TDS", icon: Percent, permission: "tds.view" },
      { to: "/compliance/itc04", label: "ITC-04", icon: ClipboardList, permission: "itc04.view" },
    ]),
  },
  {
    // Reports engine (config-driven, backed by `packages/reports-engine/`) is a
    // 1.0-withheld module (returns in 1.2). Group hidden unless the workspace
    // has the `reports` entitlement, which no 1.0 plan grants. The specific
    // analytics the kept dashboard uses (`reports.aging`) are separate server
    // procedures that stay open. @see docs/adr/0046-withheld-1.0-modules.md
    label: "Reports",
    ariaLabel: "Reports",
    module: "reports",
    items: withLiveCheck([
      { to: "/reports", label: "Reports", icon: FileBarChart },
    ]),
  },
  // The "Records" group and its only item — Audit log (`/records/audit-log`,
  // `audit.view`) — were removed from the rail (#249, ADR-0062). The page,
  // route and permission gate all stay: this is discoverability, not deletion.
  // The log is a reference surface, not a workflow — every real use arrives by
  // deep link from the row being questioned (see `cash/transfers/$movementId`),
  // which is context this list never had. The group went with the item because
  // an empty group renders a bare heading. Deliberately no sunset date: unlike
  // the Packs hide (#248), #249 is a removal, not a timed one.
  {
    label: null,
    ariaLabel: "Settings",
    items: withLiveCheck([
      { to: "/settings", label: "Settings", icon: Settings, permission: "settings.manage" },
      { to: "#", label: "Help", icon: HelpCircle, external: HELP_URL },
      { to: "#", label: "Support", icon: LifeBuoy, external: SUPPORT_URL },
    ]),
  },
];

/**
 * Nav groups visible to a workspace given its entitled modules and the member's
 * granted permissions (ADR-0023 + ADR-0030).
 *
 * - Module gate: drops groups whose `module` the company isn't entitled to.
 * - Permission gate: hides individual items whose `permission` the member
 *   doesn't hold; drops groups that become entirely empty as a result.
 * - Items without a `permission` field are always shown (unprotected surfaces).
 * - The `*` wildcard (owner/founder) passes every permission check.
 *
 * Server gates (`entitledProcedureFor`, `permissionGate`) are always the
 * authoritative floor — this client filter is display-only.
 */
export function visibleNavGroups(
  modules: readonly string[],
  permissions: readonly string[] = [],
): NavGroup[] {
  const isOwner = permissions.includes("*");

  const canSee = (permission?: string): boolean => {
    if (!permission) return true;
    if (isOwner) return true;
    return permissions.includes(permission);
  };

  const hasModule = (module?: string): boolean => !module || modules.includes(module);

  return NAV_GROUPS
    .filter((group) => hasModule(group.module))
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => canSee(item.permission) && hasModule(item.module)),
    }))
    .filter((group) => group.items.length > 0);
}

/**
 * The content nav group that owns a pathname — drives the topbar's section
 * tabs and the module switcher's "you are here" highlight. Workspace-level
 * items (Home / Ask Flint / Inbox) and Settings are standalone surfaces, so
 * they never claim a group.
 */
export function findActiveGroup(groups: NavGroup[], pathname: string): NavGroup | null {
  for (const group of groups) {
    if (group.ariaLabel === "Workspace" || group.ariaLabel === "Settings") continue;
    const match = group.items.some((item) => {
      if (item.disabled || item.external) return false;
      if (item.to === "/") return false;
      return pathname === item.to || pathname.startsWith(`${item.to}/`);
    });
    if (match) return group;
  }
  return null;
}
